// 6a-2 — the "Analytics go-live" checklist items + helpers, MIRRORED from the server source of
// truth (netlify/functions/_lib/analytics/golive.ts) so the admin UI can render the checklist +
// progress without crossing the src↔netlify tsconfig boundary (same pattern as clientTokens.ts /
// sections.ts). Keep the item keys + list in lock-step with the server module.
export type ClientType = 'video' | 'photo' | null;

export interface GoLiveItem {
  key: string;
  label: string;
  help: string;
  verify?: 'plausible' | 'gsc' | 'timezone';
}

export function goLiveItems(clientType: ClientType): GoLiveItem[] {
  const goalsLabel =
    clientType === 'video'
      ? 'Create goals: Contact + Video Play'
      : clientType === 'photo'
        ? 'Create goals: Contact + Gallery Open + View Image'
        : 'Create goals (set the client type first)';
  const goalsHelp =
    clientType === null
      ? 'Choose Video or Photo above to see the standard goal kit for this client.'
      : 'Add each goal as a custom event in Plausible → Site settings → Goals. The built site fires these events by construction (6a-1 kit).';
  return [
    { key: 'plausible_site', label: 'Plausible site created + auto-measurements ON', help: 'Add the site in Plausible and enable outbound links / file downloads / 404s / form submissions (auto-measurements). Sets the Plausible domain in the manifest.', verify: 'plausible' },
    { key: 'goals', label: goalsLabel, help: goalsHelp },
    { key: 'gsc', label: 'Search Console linked via DNS', help: 'Verify the property in Google Search Console via the Cloudflare DNS TXT method (§12) under the Alchmy account — no client login. Sets the GSC property in the manifest.', verify: 'gsc' },
    { key: 'ip_exclude', label: "Alchmy's IP excluded (+ the client's if known)", help: "Add an IP-block exclusion in Plausible for Alchmy's IP (always) and the client's too if you know it, so report numbers are real visitors only." },
    { key: 'emails_off', label: 'Plausible email reports off', help: 'Turn off Plausible weekly/monthly email reports — the reporting console is the sole sender.' },
    { key: 'timezone', label: "Timezone set (client's local)", help: "Set the client's local timezone so report period boundaries are correct. Sets timezone in the manifest.", verify: 'timezone' },
    { key: 'utm', label: 'Marketing links UTM-tagged', help: "UTM-tag the client's marketing links (social bios, campaigns) so the journey/attribution report can name the channel (§14.0)." },
  ];
}

export function goLiveProgress(golive: Record<string, boolean> | null | undefined, clientType: ClientType): { done: number; total: number } {
  const items = goLiveItems(clientType);
  const g = golive ?? {};
  return { done: items.filter((item) => g[item.key] === true).length, total: items.length };
}

export function goLiveComplete(golive: Record<string, boolean> | null | undefined, clientType: ClientType): boolean {
  if (!clientType) return false;
  const g = golive ?? {};
  return goLiveItems(clientType).every((item) => g[item.key] === true);
}
